import Button from '@mui/material/Button';
import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import {
    memberRelationsQueryOptions,
    useCreateMemberRelationMutation,
    useDeleteMemberRelationMutation,
    useUpdateMemberRelationMutation,
} from '../../api/mem/memberRelationQueryOptions';

import LoadProgress from '@/components/LoadProgress';
import { Grid } from '@mui/material';

import { GridColDef } from '@mui/x-data-grid';

import {
    defaultMemberRelation,
    memberRelationSchema,
    MemberRelationType,
} from '@/api/mem/memberRelation';
import { fetchDetailPermissionsQueryOptions } from '@/api/permissionsQueryOptions';
import CreateDetailAccordion, {
    useCreateDetail,
} from '@/components/detail/CreateDetailRecord';
import EditGrid, { useEditGrid } from '@/components/detail/EditGrid';
import useBasicGridColumns from '@/components/grid/BasicGridColumns';
import useDetailCrudEvents from '@/components/hooks/useDetailCrudEvents';
import useResponsive from '@/components/hooks/useResponsive';
import ScoregCheckbox from '@/components/mui/ScoregCheckbox';
import ScoutIdAutocompleteField from '@/components/ScoutIdAutocompleteField';
import SelectRelationType from '@/components/select/SelectRelationType';
import { yupResolver } from '@hookform/resolvers/yup';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { FormLabel, TextField, Tooltip } from '@mui/material';
import i18next, { t } from 'i18next';

export const Route = createFileRoute(
    '/_auth/member_/$memberUuid/relations'
)({
    component: MemberRelationComponent,
})

function MemberRelationComponent() {
    const params = Route.useParams()
    const memberRelationsQuery = useSuspenseQuery(
        memberRelationsQueryOptions(params.memberUuid),
    )
    const memberRelations = memberRelationsQuery.data

    const permissionsQuery = useSuspenseQuery(
        fetchDetailPermissionsQueryOptions('member', params.memberUuid, [
            'MemberRelation',
        ]),
    )
    const permissions = permissionsQuery.data

    const createable =
        permissions &&
        permissions.filter(
            (p) =>
                p.detail === 'MemberRelation' && p.permissions.some((a) => a === 'create'),
        ).length > 0

    const { onCreated, onUpdated, onDeleted, onError } =
        useDetailCrudEvents<MemberRelationType>('memberRelation')

    const createMemberRelationMutation = useCreateMemberRelationMutation(
        params.memberUuid,
        onCreated,
        onError,
    )
    const updateMemberRelationMutation = useUpdateMemberRelationMutation(
        params.memberUuid,
        onUpdated,
        onError,
    )
    const deleteMemberRelationMutation = useDeleteMemberRelationMutation(
        params.memberUuid,
        onDeleted,
        onError,
    )

    const basicGridColumns = useBasicGridColumns()

    const onSubmit = (data: MemberRelationType) => {
        createMemberRelationMutation.mutate(data)
    }

    const { large, xlarge } = useResponsive()

    const columns: GridColDef[] = []
    columns.push({
        field: 'relation_name',
        valueGetter: (_value, row) =>
            `${row.relation.firstname} ${row.relation.lastname}`,
        renderCell: (params) => {
            return (
                <Link
                    to="/member/$memberUuid/main"
                    params={{
                        memberUuid: params.row.relation.uuid,
                    }}
                    preload="intent"
                >
                    {params.row.relation.firstname} {params.row.relation.lastname}
                </Link>
            )
        },
        headerName: i18next.t('property.memberRelation.relation'),
        minWidth: 150,
    })
    columns.push({
        field: 'type',
        valueGetter: (_value, row) => `${row.type}`,
        headerName: i18next.t('property.memberRelation.type'),
        minWidth: 75,
        type: 'singleSelect',
        valueOptions: [
            'WIFE',
            'HUSBAND',
            'SISTER',
            'BROTHER',
            'DAUGHTER',
            'SON',
            'MOTHER',
            'FATHER',
            'SIBLING',
            'KID',
            'PARTNER',
            'LEGAL_PARENT',
            'LEGAL_KID',
            'CONTACT',
        ],
        getOptionLabel: (value) => {
            return t('enum.memberRelation.type.' + (value || 'null'))
        },
        valueFormatter(value) {
            return t('enum.memberRelation.type.' + (value || 'null'))
        },
        editable: true

    })
    if (large || xlarge) {
        columns.push({
            field: 'paymentRelevant',
            valueGetter: (_value, row) => {
                return true === row.paymentRelevant
            },
            headerName: i18next.t('property.memberRelation.paymentRelevant'),
            minWidth: 50,
            type: 'boolean',
            editable: true,


        })
    }
    if (large || xlarge) {
        columns.push(basicGridColumns.descriptionColumn)
    }

    const createDetail = useCreateDetail<MemberRelationType>({
        onSubmit,
        resolver: yupResolver<MemberRelationType, any, any>(memberRelationSchema),
        defaultValues: {
            ...defaultMemberRelation,
            member: {
                uuid: params.memberUuid,
            },
        },
        title: t('action.assign', { entity: t('entity.relation') }),
    })

    const editGrid = useEditGrid<MemberRelationType>({
        defaultValues: defaultMemberRelation,
        updateMutation: updateMemberRelationMutation,
        deleteMutation: deleteMemberRelationMutation,
        records: memberRelations,
        columns: columns,
        title: i18next.t('entity.relation'),
    })

    if (memberRelationsQuery.isLoading || memberRelationsQuery.isFetching) {
        return <LoadProgress />
    }

    return (
        <>
            {/*JSON.stringify({
            xs: xsmall,
            sm: small,
            md: medium, 
            lg: large,
            xl: xlarge,
          })*/}
        {createable && (
            <Grid container
                spacing={2}
                sx={{
                    justifyContent: "flex-end",
                    height: 50
                }}>
                <Link
                    to={'/createMember'}
                    search={{
                        relatedMemberUuid: params.memberUuid,
                    }}
                    activeProps={{
                        className: 'scoreg-active-button',
                    }}
                    activeOptions={{
                        includeSearch: false,
                    }}
                >
                    <Tooltip placement="left" title="Familienmitglied anlegen">
                        <Button endIcon={<AddCircleIcon />} variant="text">
                            Familienmitglied anlegen
                        </Button>

                    </Tooltip>
                </Link>
            </Grid>
        )}
            {createable && (<CreateDetailAccordion {...createDetail}>
                <Grid container spacing={2}>
                    <input
                        type="hidden"
                        defaultValue={params.memberUuid}
                        {...createDetail.form.register('member.uuid')}
                    />
                    <Grid
                        size={{
                            xs: 12,
                            md: 6,
                            lg: 4
                        }}>
                        <SelectRelationType control={createDetail.form.control} name="type"/>
                    </Grid>
                    <Grid
                        size={{
                            xs: 12,
                            md: 1
                        }}
                        style={{ paddingTop: 25 }}>
                        <FormLabel>ist</FormLabel>
                    </Grid>
                    <Grid
                        size={{
                            xs: 12,
                            md: 6,
                            lg: 4
                        }}>
                        <ScoutIdAutocompleteField
                            name="relation"
                            defaultValue={defaultMemberRelation.member.uuid}
                            control={createDetail.form.control}
                            errors={createDetail.form.formState.errors}
                        ></ScoutIdAutocompleteField>
                    </Grid>
                    <></>
                </Grid>
                <Grid container spacing={2}>
                    <Grid
                        size={{
                            xs: 12,
                            md: 6,
                            lg: 4
                        }}>
                        <ScoregCheckbox defaultValue={true} name="paymentRelevant" control={createDetail.form.control} label={t('property.memberRelation.paymentRelevant')} />
                    </Grid>
                </Grid>
                <Grid container spacing={2}>
                    <Grid
                        size={{
                            xs: 12,
                            md: 12,
                            lg: 6
                        }}>
                        <TextField
                            {...createDetail.form.register('description')}
                            label={t('property.general.description')}
                            multiline
                            rows={2} />
                    </Grid>
                </Grid>
            </CreateDetailAccordion>
            )}
            <EditGrid {...editGrid} />
        </>
    );
}
